<template>
  <section class="select">
    <div @click="open = true" class="select-field">
      <input
        :value="textValue"
        class="field"
        type="text"
        size="1"
        :placeholder="values.length === 0 || !multiple ? placeholder : ''"
        readonly
      />
      <i class="fas fa-angle-down field-icon"></i>
      <div v-if="multiple" class="selected-list">
        <div v-for="v in values" @click.stop="select(v)" :key="v" class="selected-list-item">
          <span>{{ options.find(x => x.value === v) ? options.find(x => x.value === v).text : '-' }}</span>
          <i class="fas fa-times"></i>
        </div>
      </div>
    </div>
    <transition name="popup">
      <div v-show="open" @click="close" class="popup-overlay">
        <div class="popup">
          <div @click.stop class="select-field-search">
            <!-- Fix mobile type not changing when v-model -->
            <input :value="search" @input="e => search = e.target.value" ref="search" type="text" class="field" />
            <!-- <input v-model="search" ref="search" type="text" class="field" /> -->
            <template v-if="multiple">
              <i class="fas fa-search field-icon" style="right: 70px"></i>
              <i @click="selectAll" class="fas fa-check field-icon" style="right: 40px; cursor: pointer"></i>
              <i @click="close" class="fas fa-times field-icon" style="cursor: pointer"></i>
            </template>
            <template v-else>
              <i class="fas fa-search field-icon" style="right: 40px"></i>
              <i @click="close" class="fas fa-times field-icon" style="cursor: pointer"></i>
            </template>
          </div>
          <div ref="list" class="select-list">
            <div
              v-for="(option, i) in filter"
              @click.stop="select(option.value, $event)"
              :key="i"
              class="select-list-item"
            >
              <div class="select-text">
                <span>{{ option.text }}</span>
                <span v-if="option.detail">{{ option.detail }}</span>
              </div>
              <i v-if="option.value === value || (multiple && values.includes(option.value))" class="fas fa-check"></i>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
export default {
  name: 'Select',
  inheritAttrs: false,
  props: ['value', 'options', 'multiple', 'placeholder'],
  data () {
    return {
      open: false,
      search: '',
      limit: 25
    }
  },
  computed: {
    filter () {
      return this.search
        ? this.options.filter(x => String(x.text).toUpperCase().indexOf(this.search.toUpperCase()) > -1).filter((x, i) => i <= this.limit)
        : this.options.filter((x, i) => i <= this.limit)
    },
    rawFilter () {
      return this.options.filter(x => String(x.text).toUpperCase().indexOf(this.search.toUpperCase()) > -1)
    },
    textValue () {
      return this.value && !this.multiple && this.options.find(x => x.value === this.value)
        ? this.options.find(x => x.value === this.value).text
        : ''
    },
    values () {
      const { value } = this
      return value && this.multiple ? value.sort((a, b) => b - a) : []
    }
  },
  watch: {
    open (value) {
      if (value) setTimeout(() => { this.$refs.search.focus() }, 800)
    }
  },
  mounted () {
    this.$nextTick(() => {
      if (this.$refs.list) {
        this.$refs.list.onscroll = (e) => {
          const rowHeight = this.$refs.list.firstElementChild.offsetHeight
          const scrollTop = e.target.scrollTop
          const index = Math.floor(scrollTop / rowHeight)
          const visible = Math.ceil(e.target.offsetHeight / rowHeight)
          this.limit = index + visible * 5
        }
      }
    })
  },
  methods: {
    select (optionValue) {
      if (this.multiple) {
        let { value } = this
        if (value.includes(optionValue)) {
          value = value.filter(x => x !== optionValue)
        } else {
          value.push(optionValue)
        }
        this.$emit('input', value)
        this.$emit('change', value)
      } else {
        this.$emit('input', optionValue)
        this.$emit('change', optionValue)
      }
      if (!this.multiple) this.close()
    },
    close () {
      setTimeout(() => {
        this.open = false
        this.search = ''
      }, 200)
    },
    selectAll () {
      if (this.value.length > 0) {
        this.$emit('input', [])
        this.$emit('change', [])
      } else {
        this.$emit('input', this.rawFilter.map(x => x.value))
        this.$emit('change', this.rawFilter.map(x => x.value))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../_asset/scss/_variables";

.select {
  width: 100%;
}

.select-field {
  position: relative;
  width: 100%;
  cursor: pointer;
  .field {
    width: 100%;
    padding-right: 40px;
    cursor: pointer;
  }
  .field-icon {
    color: $primary-color;
    position: absolute;
    top: 13px;
    right: 0;
    width: 20px;
    height: 20px;
  }
}

.popup-overlay {
  position: absolute;
  background: transparent;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  display: flex;
  justify-content: center;
  align-items: center;
}

.popup {
  background: #fff;
  padding-bottom: 10px;
  margin: 0;
  width: calc(100% - 40px);
  max-width: initial;
  max-height: calc(100vh - 40px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.175);
  border: 1px solid #e8e8e8;
}

.select-field-search {
  position: relative;
  .field {
    border: none;
    width: calc(100% - 70px);
  }
  .field-icon {
    color: $primary-color;
    top: 13px;
    width: 20px;
    height: 20px;
    &:first-of-type {
      color: #bbb;
    }
  }
}

.select-list {
  background-color: #fff;
  border-top: 1px solid #ccc;
  border-bottom: 1px solid #ccc;
  overflow-y: scroll;
  height: calc(100% - 50px);
}

.select-list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  font-size: 1em;
  border-bottom: 1px solid #ddd;
  cursor: pointer;
  &:hover {
    background-color: $hover-color;
  }
  i {
    width: 20px;
    height: 20px;
    color: $primary-color;
  }
}

.selected-list {
  position: absolute;
  top: 3px;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  margin-left: 5px;
  margin-right: 40px;
  overflow-x: hidden;
}

.selected-list-item {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  background-color: $hover-color;
  color: $primary-color;
  border-radius: 2px;
  padding: 2px 3px 2px 8px;
  margin-left: 10px;
  &:first-of-type {
    margin-left: 0;
  }
  i {
    margin-left: 5px;
    margin-right: 5px;
  }
}

.select-text {
  span {
    display: block;
    text-align: left;
  }
  span:nth-of-type(2) {
    font-size: 11px;
    color: #999;
  }
}

.popup-enter-active {
  animation: popup 0.4s;
}

.popup-leave-active {
  animation: popup 0.4s reverse;
}

@keyframes popup {
  0% { transform: scale(0) }
  100% { transform: scale(1) }
}

@media screen and (min-width: $desktop-width) {

  .popup {
    margin: 15px;
    width: calc(100% - 10px);
    max-width: 500px;
  }

  .select-list {
    max-height: 200px;
  }

}
</style>
